import { useSiteImage, useSiteText } from "@/hooks/useSiteImages";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import { LeadMagnetDialog } from "@/components/LeadMagnetDialog";
import { IntroOfferBanner } from "@/components/IntroOfferBanner";
import { upcomingDates } from "@/components/OralChampionSection";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  ShieldCheck, GraduationCap, FileCheck2, Video, Users, CalendarClock,
  Star, ArrowRight, CheckCircle2, Award, Facebook, Stethoscope, ClipboardCheck,
} from "lucide-react";
import { SelfCheckDialog } from "@/components/SelfCheckDialog";
import { openAskJane } from "@/components/AdvisorWidget";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Landing() {
  const t = useSiteText();
  const imgHero = useSiteImage("hero", "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/15899d471051a790_assisted.jpg");
  const imgOC = useSiteImage("oral_champion", "https://static.prod-images.emergentagent.com/jobs/ad91364e-1619-4fc4-bcc3-13d0678bb749/images/13dc9cd0d62a69fa98f09784990df89326a07cc1963080c3c39e65757a174262.jpeg");
  const imgExperts = useSiteImage("experts", "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/f6225276dcc7be7d_The%20Photography%20Lounge%20%28high%20res%29%20040.webp");
  const imgTT = useSiteImage("train_the_trainer", "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/1bbedb18ede23ca6_AaE-seOx4Qd1p7Y_2P8-fg-AaE-seOxdn-yHPGxNMI0Lg.jpg");
  const nav = useNavigate();
  const [leadOpen, setLeadOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const nextCourse = (upcomingDates()[0]?.label || "Dates announced monthly").replace(/, 10am.*$/, "");

  useEffect(() => { api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}); }, []);

  const features = [
    { icon: Video, title: "Train Your Whole Team", desc: "Accessible oral healthcare training and certificates." },
    { icon: CalendarClock, title: "Develop an Oral Champion", desc: "Live expert-led sessions to build internal knowledge and confidence." },
    { icon: FileCheck2, title: "Strengthen Compliance", desc: "Policies, checklists and practical evidence resources." },
    { icon: ClipboardCheck, title: "Audit & Improve", desc: "Tools to review practice, identify gaps and track improvement priorities." },
    { icon: Users, title: "Professional Support When You Need It", desc: "Get answers to your oral healthcare training and compliance questions from the KOHC team." },
  ];

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav onOpenLead={() => setLeadOpen(true)} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fade} className="lg:col-span-7">
            <span className="label-caps text-coral">{t("hero_eyebrow", "Oral healthcare compliance, from dental care professionals")}</span>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand tracking-tight mt-4 leading-[1.05]">
              {t("hero_title", "Could your care home evidence good oral healthcare to the")} <span className="text-coral">{t("hero_highlight", "CQC tomorrow?")}</span>
            </h1>
            <p data-testid="hero-tagline" className="text-lg md:text-xl font-heading font-semibold text-brand mt-6 inline-flex items-start gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-coral animate-pulse mt-2 flex-shrink-0" />
              <span>{t("hero_tagline", "Training, compliance and expert support for care homes — all in one place.")}</span>
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl" data-testid="hero-benefits">
              {["Staff Training", "Your Own Oral Health Champion", "Compliance & Audit Resources", "Ongoing Professional Support"].map((title) => (
                <li key={title} className="flex items-center gap-3 font-semibold text-brand">
                  <CheckCircle2 className="h-5 w-5 text-coral flex-shrink-0" />{title}
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl border border-cream-deep bg-cream-card p-5 max-w-xl" data-testid="hero-next-step">
              <div className="font-heading font-bold text-brand">Not sure where your service stands?</div>
              <p className="text-sm text-muted-foreground mt-1.5">Take our free 3-minute assessment to discover potential gaps in your oral healthcare practice and inspection evidence.</p>
              <Button data-testid="hero-selfcheck-btn" onClick={() => setCheckOpen(true)} className="w-full h-12 mt-4 rounded-xl bg-coral hover:bg-coral-hover text-white text-base font-semibold">
                Take the Free 3-Minute Assessment <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">15 questions · Approximately 3–4 minutes · Free</p>
              <div className="border-t border-cream-deep my-4" />
              <Button data-testid="hero-trial-btn" variant="outline" onClick={() => nav("/signup")} className="w-full h-12 rounded-xl border-cream-deep bg-white text-brand hover:bg-brand hover:text-white text-base font-semibold">
                Start your free 14-day trial <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">Up to 4 staff can complete the e-learning training free for 14 days.</p>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-5">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-cream-deep shadow-xl">
                <img data-testid="hero-image" src={imgHero} alt="Care worker helping an elderly resident with oral care in a care home" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-cream-card rounded-2xl border border-cream-deep p-4 shadow-lg w-64" data-testid="hero-champion-card">
                <div className="label-caps text-muted-foreground whitespace-nowrap">Your Oral Health Champion</div>
                <div className="flex items-center gap-2 mt-1.5"><Award className="h-6 w-6 text-rag-green" /><span className="text-2xl font-bold text-brand font-heading">LIVE Training</span></div>
                <div className="flex items-center gap-1.5 text-sm text-rag-green mt-2 whitespace-nowrap"><CheckCircle2 className="h-4 w-4" />Included in your subscription</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ORAL CHAMPION — flagship */}
      <section className="bg-brand text-white py-24" data-testid="oral-champion-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div {...fade} className="lg:col-span-6 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl relative">
                <img src={imgOC} alt="Jane delivering a live Oral Champion training webinar" className="w-full h-[460px] object-cover" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-coral text-white text-xs font-semibold px-3 py-1.5"><span className="h-2 w-2 rounded-full bg-white animate-pulse" />Live · taught by a dental care professional</div>
              </div>
            </motion.div>
            <motion.div {...fade} className="lg:col-span-6 order-1 lg:order-2">
              <span className="label-caps text-coral">{t("oc_eyebrow", "The flagship · included with every licence")}</span>
              <h2 data-testid="oral-champion-title" className="text-3xl md:text-5xl font-bold tracking-tight mt-3">{t("oc_title", "Turn one of your team into an Oral Health Champion")}</h2>
              <p className="text-white/75 mt-5 leading-relaxed">{t("oc_body", "Training only works if it’s used on the floor. Your Oral Health Champion makes sure it is: promoting good mouth care, supporting colleagues, spotting concerns early and keeping oral health on the agenda. With live training and ongoing support from KOHC, they’ll know what to do and when to ask.")}</p>
              <ul className="mt-6 space-y-4">
                {[
                  ["Live 3-hour Oral Champion course", "Taught online by Jane — a dental care professional, not a presenter reading slides. Real cases, real mouths, real questions answered."],
                  ["Certificate and Champion toolkit", "Certificate on completion, plus the assessment and care-planning tools your Champion will use every week."],
                  ["Ongoing support, not a one-off", "Direct access to Jane through Ask Jane whenever a tricky case comes up, plus the Oral Champion Community Hub."],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-coral flex-shrink-0 mt-0.5" />
                    <div><div className="font-semibold">{t}</div><div className="text-sm text-white/70 mt-0.5">{d}</div></div>
                  </li>
                ))}
              </ul>
              <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="next-live-sessions">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="label-caps text-white/60">Next Oral Champion course</div>
                  <div className="font-heading font-bold text-lg mt-1">{nextCourse}</div>
                  <div className="text-xs text-white/60">3 hours live · one place included</div>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="label-caps text-white/60">Ask Jane</div>
                  <div className="font-heading font-bold text-lg mt-1">Specialist support on tap</div>
                  <div className="text-xs text-white/60">Message Jane from your dashboard whenever you need advice</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                <Button data-testid="oral-champion-btn" onClick={() => nav("/oral-champion")} className="h-11 px-6 rounded-full bg-coral hover:bg-coral-hover text-white">
                  Train your Oral Champion <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button data-testid="live-sessions-btn" onClick={() => nav("/signup")} variant="outline" className="h-11 px-6 rounded-full border-white/40 text-white bg-transparent hover:bg-white hover:text-brand transition-colors duration-200">
                  Start free trial
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXPERTS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="experts-section">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fade} className="lg:col-span-7">
            <span className="label-caps text-coral">{t("experts_eyebrow", "Who you're learning from")}</span>
            <h2 data-testid="experts-title" className="text-3xl md:text-4xl font-bold text-brand tracking-tight mt-3">{t("experts_title", "Meet Your Oral Healthcare Expert")}</h2>
            <div className="mt-5" data-testid="experts-credentials">
              <div className="font-heading font-bold text-xl text-brand">{t("experts_name", "Jane Peterson BSc, RDH")}</div>
              <div className="text-sm text-muted-foreground mt-1">{t("experts_credential", "Registered Dental Hygienist | GDC Registration No. 4846")}</div>
              <div className="text-sm text-muted-foreground">{t("experts_role", "Founder of Knowledge Oral HealthCare (KOHC)")}</div>
            </div>
            <p className="text-muted-foreground mt-5 leading-relaxed">{t("experts_body1", "With over a decade of experience specialising in oral healthcare within the care sector, Jane combines her clinical background as a registered dental hygienist with a passion for improving oral healthcare standards in care settings.")}</p>
            <p className="text-muted-foreground mt-3 leading-relaxed">{t("experts_body2", "Since 2015, she has worked with care professionals to develop practical, accredited training that builds staff knowledge, skills and confidence.")}</p>
            <p className="text-muted-foreground mt-3 leading-relaxed">{t("experts_body3", "Through KOHC, Jane now brings together staff training, live Oral Health Champion development, compliance resources, audit tools and ongoing professional support to help care providers deliver and demonstrate good oral healthcare.")}</p>
            <ul className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                [Stethoscope, "Clinically led", "Content written and taught by dental care professionals"],
                [ShieldCheck, "CQC & NICE aligned", "Mapped to NICE NG48 and CQC oral health expectations"],
                [Users, "700+ care homes", "Trained across the UK since we started"],
              ].map(([Icon, t, d]) => (
                <li key={t} className="rounded-2xl bg-cream-card border border-cream-deep p-4">
                  <Icon className="h-5 w-5 text-coral" />
                  <div className="font-semibold text-brand mt-2">{t}</div>
                  <div className="text-xs text-muted-foreground mt-1">{d}</div>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-cream-deep shadow-lg">
              <img data-testid="experts-photo" src={imgExperts} alt="Jane, dental care professional and founder of KOHC, in her dental surgery" className="w-full h-[480px] object-cover object-top" />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div>
                <div className="font-heading font-bold text-brand">Jane Peterson BSc, RDH</div>
                <div className="text-sm text-muted-foreground">Registered Dental Hygienist · Founder, KOHC</div>
              </div>
              <Button data-testid="experts-ask-btn" variant="outline" size="sm" onClick={() => openAskJane("")} className="ml-auto rounded-full border-brand text-brand hover:bg-brand hover:text-white">Ask Jane a question</Button>
            </div>
          </motion.div>
        </div>
        <motion.div {...fade} className="mt-10 rounded-2xl bg-cream-card border border-cream-deep px-5 md:px-8 py-5 flex flex-col lg:flex-row lg:items-center gap-5" data-testid="awards-strip">
          <div className="lg:max-w-sm">
            <span className="label-caps text-coral">Professional recognition</span>
            <div className="font-heading font-bold text-brand text-lg mt-1">Award-Winning Oral Healthcare Education</div>
            <p className="text-sm text-muted-foreground mt-1">Oral Health Awards (Philips Shine On) 2019–2021 and Care Sector Supplier Awards 2021.</p>
          </div>
          <ul className="flex flex-wrap items-center justify-start lg:justify-end gap-6 md:gap-8 lg:ml-auto">
            {[
              ["/awards/oha-2019-highly-commended.webp", "Oral Health Awards 2019 — Highly Commended, Philips Shine On Award", "2019", "Highly Commended"],
              ["/awards/oha-2020-winner.png", "Oral Health Awards 2020 — Winner, Philips Shine On Award", "2020", "Winner"],
              ["/awards/oha-2021-winner.png", "Oral Health Awards 2021 — Winner, Philips Shine On Award", "2021", "Winner"],
              ["/awards/csa-2021-winner.webp", "Care Sector Supplier Awards 2021 — Winner", "2021", "Winner"],
            ].map(([src, alt, year, label]) => (
              <li key={alt} className="flex flex-col items-center text-center" data-testid={`award-${year}-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <img src={src} alt={alt} title={alt} loading="lazy" className="h-16 md:h-20 w-auto object-contain transition-transform duration-200 hover:scale-105" />
                <span className="text-xs font-semibold text-brand mt-2">{year}</span>
                <span className="text-[11px] text-muted-foreground">{label}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* FOUNDING / INTRO OFFER BANNER */}
      <IntroOfferBanner variant="banner" />

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <motion.div {...fade} className="max-w-2xl mb-12">
          <span className="label-caps text-coral">Everything Your Care Service Needs, In One Place</span>
          <h2 data-testid="features-title" className="text-3xl md:text-4xl font-bold text-brand tracking-tight mt-3">{t("features_title", "Everything your care home needs for oral-health compliance — in one place.")}</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-cream-card border border-cream-deep rounded-2xl p-7 transition-transform duration-150 hover:-translate-y-1 hover:shadow-md">
              <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center mb-5"><f.icon className="h-5 w-5 text-brand" /></div>
              <h3 className="text-lg font-bold text-brand">{f.title}</h3>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-cream-deep/40 py-20">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <motion.div {...fade} className="mb-12">
              <span className="label-caps text-coral">Trusted by care teams</span>
              <h2 className="text-3xl md:text-4xl font-bold text-brand tracking-tight mt-3">{t("testimonials_title", "Homes that made oral health effortless")}</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((t, i) => (
                <motion.div key={t.id} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`testimonial-${i}`} className="bg-cream-card border border-cream-deep rounded-2xl p-7">
                  <div className="flex gap-1 mb-4">{Array.from({ length: t.rating }).map((_, k) => <Star key={`star-${k}`} className="h-4 w-4 fill-coral text-coral" />)}</div>
                  <p className="text-foreground leading-relaxed">“{t.quote}”</p>
                  <div className="mt-5 pt-5 border-t border-cream-deep">
                    <div className="font-semibold text-brand">{t.name}</div>
                    <div className="text-sm text-muted-foreground">{t.care_home}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRAIN THE TRAINER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fade} className="rounded-3xl overflow-hidden border border-cream-deep order-2 lg:order-1">
          <img src={imgTT} alt="A learner following a KOHC oral health webinar on her laptop" className="w-full h-[380px] object-cover" />
        </motion.div>
        <motion.div {...fade} className="order-1 lg:order-2">
          <span className="label-caps text-coral">Premium · sold separately</span>
          <h2 className="text-3xl md:text-4xl font-bold text-brand tracking-tight mt-3">{t("tt_title", "Become an Oral Health Train-the-Trainer")}</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">{t("tt_body", "A separate, application-gated professional qualification. Deliver oral-health training in your own organisation — and optionally join the KOHC trainer roster to deliver live sessions.")}</p>
          <ul className="mt-6 space-y-3">
            {["Own certificate & CPD hours", "Application-reviewed cohort", "Route onto the paid trainer roster"].map((x) => (
              <li key={x} className="flex items-center gap-3"><GraduationCap className="h-5 w-5 text-brand" /><span>{x}</span></li>
            ))}
          </ul>
          <Button data-testid="tt-learn-btn" onClick={() => nav("/train-the-trainer")} className="mt-8 h-12 px-8 rounded-full bg-brand hover:bg-brand-hover text-white">Explore Train the Trainer</Button>
        </motion.div>
      </section>

      {/* FACEBOOK / CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-10">
        <motion.div {...fade} className="rounded-3xl bg-cream-card border border-cream-deep p-10 md:p-14 text-center">
          <ShieldCheck className="h-12 w-12 text-coral mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-bold text-brand tracking-tight">{t("cta_title", "Ready to fix your oral-health compliance?")}</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{t("cta_body", "Start a free trial today, add your team, and see your compliance dashboard fill in real time.")}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Button data-testid="cta-trial-btn" onClick={() => nav("/signup")} className="h-12 px-8 rounded-full bg-coral hover:bg-coral-hover text-white">Start free trial</Button>
            <a href="https://www.facebook.com/groups/27941660172109456" target="_blank" rel="noreferrer">
              <Button variant="outline" className="h-12 px-8 rounded-full border-brand text-brand hover:bg-brand hover:text-white"><Facebook className="h-4 w-4 mr-2" />Join the Oral Champion Community Hub</Button>
            </a>
          </div>
          <p className="text-sm text-muted-foreground mt-4">All Oral Champions welcome — open to everyone who has completed any of our training courses.</p>
        </motion.div>
      </section>

      <PublicFooter />
      <LeadMagnetDialog open={leadOpen} onOpenChange={setLeadOpen} />
      <SelfCheckDialog open={checkOpen} onOpenChange={setCheckOpen} />
    </div>
  );
}
